/**
 * @fileoverview The mailbox rent-payment vocabulary (MAILBOX-RENT-PAYMENTS-001; owner rulings R-SSC-3 and
 * R-MRP-1…4): a rent-payment ATTEMPT's lifecycle (`mailbox_rent_payments.status`) and an autopay AUTHORIZATION's
 * lifecycle (`mailbox_autopay_authorizations.status`). Declared once for pos-api (the rent sale, the relay route, the
 * autopay and notices sweeps), purchase-api (the quote, the Connect webhook handler) and the web (the pay page, the
 * holder's account view, pos-web's rent panel), so no app spells a payment's state for itself.
 *
 * ## The attempt machine
 *
 * A payment attempt is created `pending` when a PaymentIntent is created (online or by the autopay sweep) or when the
 * register rings a rent sale. Stripe's webhook, or the register's tender, moves it to `succeeded` — the ONE moment the
 * rental renews — or to `failed`. A `succeeded` attempt may later be `refunded` (the renewal is reversed with it), and a
 * `refunded` attempt is REINSTATED to `succeeded` when the refund that reversed it is finalised `failed` at the gateway
 * (the money stayed with the business, so the renewal it bought is reapplied — MAILBOX-RENT-PAYMENTS-001 U3). A
 * `failed` attempt is history: the sweep's next step is a NEW attempt, never a re-run of this one. Every status is a
 * KEY, a terminal one mapping to `[]`, the `SHIPMENT_STATUS_TRANSITIONS` convention.
 *
 * ## The authorization machine
 *
 * A holder's stored-credential authorization is `active` from the moment they tick the box and the text is recorded
 * (R-MRP-3 as amended: never pre-ticked). The holder, or the counter, may `revoke` it. A hard decline, or exhausted
 * retries, `suspend`s it: the card is unusable and the holder is told; a new card makes a NEW authorization, so
 * `suspended` moves only to `revoked`. Revoked is terminal.
 *
 * @module @puffin-core/status-taxonomy/mailboxRentPayment
 */

'use strict';

const { BADGE_TONE } = require('./badgeTones');

/** The empty answer, shared so a caller can never widen a status's targets through it. */
const NO_TARGETS = Object.freeze([]);

/**
 * A rent-payment attempt's status.
 *
 * @type {Readonly<Object<string, string>>}
 */
const MAILBOX_RENT_PAYMENT_STATUS = Object.freeze({
  PENDING: 'pending',
  SUCCEEDED: 'succeeded',
  FAILED: 'failed',
  REFUNDED: 'refunded',
});

/** @type {ReadonlyArray<string>} */
const MAILBOX_RENT_PAYMENT_STATUS_VALUES = Object.freeze(
  Object.values(MAILBOX_RENT_PAYMENT_STATUS),
);

const { PENDING, SUCCEEDED, FAILED, REFUNDED } = MAILBOX_RENT_PAYMENT_STATUS;

/**
 * Every legal attempt move: a pending attempt settles one way or the other; only a succeeded attempt can be refunded,
 * and only a refunded one reinstated (its refund failed at the gateway).
 *
 * @type {Readonly<Object<string, ReadonlyArray<string>>>}
 */
const MAILBOX_RENT_PAYMENT_STATUS_TRANSITIONS = Object.freeze({
  [PENDING]: Object.freeze([SUCCEEDED, FAILED]),
  [SUCCEEDED]: Object.freeze([REFUNDED]),
  [FAILED]: NO_TARGETS,
  [REFUNDED]: Object.freeze([SUCCEEDED]),
});

/**
 * An attempt's badge TONE (`<StatusBadgeCell statusTones>`, Q-MI-25): money that moved is good news, money in flight is
 * information, a failure needs attention, and a refund is closed without a verdict.
 *
 * @type {Readonly<Object<string, string>>}
 */
const MAILBOX_RENT_PAYMENT_STATUS_BADGE_TONES = Object.freeze({
  [PENDING]: BADGE_TONE.INFO,
  [SUCCEEDED]: BADGE_TONE.SUCCESS,
  [FAILED]: BADGE_TONE.DANGER,
  [REFUNDED]: BADGE_TONE.NEUTRAL,
});

/**
 * The attempt statuses that count as "this period is being paid": the autopay sweep makes no new attempt while one
 * of these exists for the period's due date.
 *
 * @type {ReadonlyArray<string>}
 */
const MAILBOX_RENT_PAYMENT_SETTLING_STATUSES = Object.freeze([PENDING, SUCCEEDED]);

/**
 * @param {string} from - the attempt's current status
 * @returns {ReadonlyArray<string>} the statuses it may move to; `[]` for a terminal or unknown status
 */
function mailboxRentPaymentStatusTargets(from) {
  return MAILBOX_RENT_PAYMENT_STATUS_TRANSITIONS[from] ?? NO_TARGETS;
}

/**
 * A stored-credential (autopay) authorization's status.
 *
 * @type {Readonly<Object<string, string>>}
 */
const AUTOPAY_AUTHORIZATION_STATUS = Object.freeze({
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  REVOKED: 'revoked',
});

/** @type {ReadonlyArray<string>} */
const AUTOPAY_AUTHORIZATION_STATUS_VALUES = Object.freeze(
  Object.values(AUTOPAY_AUTHORIZATION_STATUS),
);

const { ACTIVE, SUSPENDED, REVOKED } = AUTOPAY_AUTHORIZATION_STATUS;

/**
 * Every legal authorization move: a decline suspends, the holder revokes, a new card is a NEW authorization.
 *
 * @type {Readonly<Object<string, ReadonlyArray<string>>>}
 */
const AUTOPAY_AUTHORIZATION_STATUS_TRANSITIONS = Object.freeze({
  [ACTIVE]: Object.freeze([SUSPENDED, REVOKED]),
  [SUSPENDED]: Object.freeze([REVOKED]),
  [REVOKED]: NO_TARGETS,
});

/** An authorization's badge TONE (`<StatusBadgeCell statusTones>`). @type {Readonly<Object<string, string>>} */
const AUTOPAY_AUTHORIZATION_STATUS_BADGE_TONES = Object.freeze({
  [ACTIVE]: BADGE_TONE.SUCCESS,
  [SUSPENDED]: BADGE_TONE.WARNING,
  [REVOKED]: BADGE_TONE.NEUTRAL,
});

/**
 * @param {string} from - the authorization's current status
 * @returns {ReadonlyArray<string>} the statuses it may move to; `[]` for a terminal or unknown status
 */
function autopayAuthorizationStatusTargets(from) {
  return AUTOPAY_AUTHORIZATION_STATUS_TRANSITIONS[from] ?? NO_TARGETS;
}

module.exports = {
  MAILBOX_RENT_PAYMENT_STATUS,
  MAILBOX_RENT_PAYMENT_STATUS_VALUES,
  MAILBOX_RENT_PAYMENT_STATUS_TRANSITIONS,
  MAILBOX_RENT_PAYMENT_STATUS_BADGE_TONES,
  MAILBOX_RENT_PAYMENT_SETTLING_STATUSES,
  mailboxRentPaymentStatusTargets,
  AUTOPAY_AUTHORIZATION_STATUS,
  AUTOPAY_AUTHORIZATION_STATUS_VALUES,
  AUTOPAY_AUTHORIZATION_STATUS_TRANSITIONS,
  AUTOPAY_AUTHORIZATION_STATUS_BADGE_TONES,
  autopayAuthorizationStatusTargets,
};
